/**
 * LoginHistoryPage (iter203b) — a customer / staff-user's own login
 * history. Backed by GET /api/me/login-history, which scopes rows to
 * the authenticated caller only. Useful for spotting unfamiliar logins.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { SignIn, SignOut, ArrowClockwise, ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";

function fmtTs(v) {
  if (!v) return "—";
  try {
    const d = new Date(v.endsWith?.("Z") ? v : `${v}Z`);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(d);
  } catch { return String(v); }
}

export default function LoginHistoryPage({ user, onLogout, onLogoutAll }) {
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/login-history", { params: { limit: 100, since_days: 90 } });
      setItems(r.data.items || []);
      setEmail(r.data.email || user?.email || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load your history");
    } finally { setLoading(false); }
  }, [user?.email]);

  useEffect(() => { refresh(); }, [refresh]);

  const logoutAll = async () => {
    if (!window.confirm("Sign out of every device you're currently logged into?")) return;
    try {
      await api.post("/auth/logout-all");
      toast.success("You've been signed out of every device.");
      if (typeof onLogoutAll === "function") onLogoutAll();
      else window.location.href = "/login";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-out failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white"
         data-testid="login-history-page">
      <AdminPageHeader
        icon={<SignIn size={20} weight="fill" className="text-blue-700" />}
        title="My login history"
        subtitle={email ? `Sign-in activity for ${email}` : "Your recent sign-in activity"}
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
        testId="login-history-back"
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        {/* Security card */}
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex items-start gap-2"
             data-testid="login-history-security-card">
          <ShieldCheck size={18} weight="bold" className="text-blue-700 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-900 leading-relaxed">
            <p className="font-bold mb-0.5">See something you don't recognise?</p>
            <p>Sign out of every device below and change your password (or ask your admin to revoke your sessions).</p>
            <button onClick={logoutAll}
                    data-testid="login-history-logout-all"
                    className="mt-2 inline-flex items-center gap-1 h-7 px-2 rounded-sm text-[10px] font-bold uppercase tracking-wider
                               bg-white text-red-700 border border-red-200 hover:bg-red-50">
              <SignOut size={11} weight="bold" /> Sign out everywhere
            </button>
          </div>
        </div>

        {/* Refresh */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500" data-testid="login-history-count">
            {loading ? "Loading…" : `${items.length} event(s) in the last 90 days`}
          </div>
          <button onClick={refresh} disabled={loading}
                  data-testid="login-history-refresh"
                  className="h-8 px-3 rounded-sm border text-xs font-bold uppercase tracking-wider
                             bg-white text-slate-700 border-slate-200 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
            <ArrowClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="login-history-table">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Method</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">IP</th>
                <th className="text-left px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={5} className="text-center py-8 text-slate-400"
                        data-testid="login-history-empty">
                  No sign-in events on record for the last 90 days.
                </td></tr>
              )}
              {items.map((it, idx) => (
                <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-slate-600">{fmtTs(it.ts_utc)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider
                      ${it.action_type === "LOGIN"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                      {it.action_type === "LOGIN"
                        ? <SignIn size={10} weight="bold" />
                        : <SignOut size={10} weight="bold" />}
                      {it.action_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 hidden sm:table-cell text-slate-600">{it.method || "—"}</td>
                  <td className="px-3 py-2 hidden md:table-cell font-mono text-[11px] text-slate-500">{it.ip_address || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{it.result || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
